import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

const focusableElementTagList = ['body', 'a', 'area'];

const focusableElementTagEnabledList = [
  'button',
  'input',
  'select',
  'textarea',
];

const focusableElementAttrList = ['tabindex'];

const focusableElementQueryList = [
  ...focusableElementTagList,
  ...focusableElementTagEnabledList.map((el) => `${el}:not(:disabled)`),
  ...focusableElementAttrList.map(
    (attr) => `[${attr}]:not([aria-hidden=true])`
  ),
];

@Injectable({ providedIn: 'root' })
export class FocusManagerService {
  private focusForm?: FormGroup;
  private focusZoneQuery?: string;
  private focusZoneElem?: HTMLElement;

  constructor(@Inject(DOCUMENT) private readonly doc: Document) {}

  setFocusForm(form: FormGroup) {
    this.focusForm = form;
  }

  setFocusQuery(queryDoc: string) {
    this.focusZoneQuery = queryDoc;
  }

  setFocusElem(elem: HTMLElement) {
    this.focusZoneElem = elem;
  }

  moveFocusStartingPoint(query: string) {
    const elToFocus = this.doc.querySelector(query) as HTMLElement;

    if (!elToFocus) {
      return;
    }

    const focusAnchor = this.doc.createElement('div');
    focusAnchor.setAttribute('tabindex', '-1');
    elToFocus.parentElement?.insertBefore(focusAnchor, elToFocus);
    focusAnchor.focus();
    focusAnchor.blur();
    focusAnchor.remove();
  }

  applyFocus() {
    if (this.focusForm) {
      this.focusFirstInForm();
    } else if (this.focusZoneElem || this.focusZoneQuery) {
      this.focusFirstElementAllowed();
    }
  }

  elementsOrdered(
    firstElem?: Element | null,
    secondElem?: Element | null
  ): boolean | null {
    // if first or second element === body or if one of them is falsy, then stop and return null
    if (
      !firstElem ||
      !secondElem ||
      firstElem === secondElem ||
      firstElem === this.doc.body ||
      secondElem === this.doc.body
    ) {
      return null;
    }

    // First, test if a sibling of first contains second and if so, check the order
    const allPreviousSiblings: Element[] = [];
    let currentPrevious: Element | null = null;
    do {
      currentPrevious = (currentPrevious || firstElem).previousElementSibling;
      if (currentPrevious) {
        allPreviousSiblings.push(currentPrevious);
      }
    } while (currentPrevious);

    const allNextSiblings: Element[] = [];
    let currentNext: Element | null = null;
    do {
      currentNext = (currentNext || firstElem).nextElementSibling;
      if (currentNext) {
        allNextSiblings.push(currentNext);
      }
    } while (currentNext);

    const foundPrevious =
      allPreviousSiblings.findIndex((previousSibling) =>
        previousSibling.contains(secondElem)
      ) > -1;
    if (foundPrevious) {
      return false;
    }

    const foundNext =
      allNextSiblings.findIndex((nextSibling) =>
        nextSibling.contains(secondElem)
      ) > -1;
    if (foundNext) {
      return true;
    }

    // If no order was found, get the parent of first and recursively call this function with second
    return this.elementsOrdered(firstElem.parentElement, secondElem);
  }

  focusFirstInForm() {
    if (!this.focusForm) {
      return;
    }

    const allFormControlNames = Object.keys(this.focusForm.controls);
    const allFormControlNodes = this.doc.querySelectorAll('[formControlName]');
    const allFormControls: HTMLElement[] = [];
    allFormControlNodes.forEach((formControlNode) => {
      if (
        allFormControlNames.includes(
          formControlNode.getAttribute('formControlName') || ''
        )
      ) {
        allFormControls.push(formControlNode as HTMLElement);
      }
    });

    // Sort the invalid fields first (false == 0, true == 1 --> false are less than true so they go in the start of the list)
    allFormControls.sort((elA, elB) => {
      const controlNameA = elA.getAttribute('formControlName') || '';
      const controlNameB = elB.getAttribute('formControlName') || '';

      const errorSort =
        +!!this.focusForm?.get(controlNameA)?.valid -
        +!!this.focusForm?.get(controlNameB)?.valid;

      if (errorSort !== 0) {
        return errorSort;
      }

      return this.elementsOrdered(elA, elB) ? -1 : 1;
    });

    // Try to focus every form control starting from the start of the list, and stop at the first valid one
    // For each control, try focusing : directly the element, then its children, then the children of its parent
    allFormControls.find((elemControlToFocus) => {
      const directElemFocus = this.tryFocusElement(elemControlToFocus);
      if (directElemFocus) {
        return true;
      }

      const controlChildrenFocus =
        this.focusInElementChildren(elemControlToFocus);
      if (controlChildrenFocus) {
        return true;
      }

      return false;
    });
  }

  focusFirstElementAllowed(
    forceQuery?: string | HTMLElement,
    allowDirectElemFocus = false
  ) {
    let usedElem: HTMLElement | null = null;

    if (forceQuery) {
      usedElem =
        typeof forceQuery === 'string'
          ? (document.querySelector(forceQuery) as HTMLElement)
          : forceQuery;
    } else if (this.focusZoneElem) {
      usedElem = this.focusZoneElem;
    } else if (this.focusZoneQuery) {
      usedElem = document.querySelector(this.focusZoneQuery) as HTMLElement;
    }

    if (!usedElem) {
      return;
    }

    if (
      getComputedStyle(usedElem)?.display === 'none' &&
      getComputedStyle(usedElem)?.visibility !== 'hidden'
    ) {
      return;
    }

    if (allowDirectElemFocus) {
      const directElemFocus = this.tryFocusElement(usedElem);
      if (directElemFocus) {
        return;
      }
    }

    // TODO - improve behavior by detecting forms and inputs in error ? detecting multiple focusable and take a specific one ?

    this.focusInElementChildren(usedElem);
  }

  private isFocusableElement(elemToFocus?: HTMLElement | null): boolean {
    if (!elemToFocus) {
      return false;
    }

    if (focusableElementTagList.includes(elemToFocus.tagName.toLowerCase())) {
      return true;
    }

    if (
      focusableElementTagEnabledList.includes(
        elemToFocus.tagName.toLowerCase()
      ) &&
      !(elemToFocus as HTMLButtonElement).disabled
    ) {
      return true;
    }

    if (
      focusableElementAttrList.findIndex((focusableAttr) =>
        elemToFocus.hasAttribute(focusableAttr)
      ) > -1
    ) {
      return true;
    }

    return false;
  }

  private tryFocusElement(elemToFocus?: HTMLElement | null): boolean {
    if (!elemToFocus) {
      return false;
    }

    if (this.isFocusableElement(elemToFocus)) {
      elemToFocus?.focus?.();
      return true;
    }

    return false;
  }

  /**
   * Function that searches for the first focusable element in the children of a given element
   * @param elemZone The element in which we should search a focusable element
   * @returns if a focusable element has been found and focused
   */
  private focusInElementChildren(elemZone?: HTMLElement | null): boolean {
    if (!elemZone) {
      return false;
    }

    const elemChildToFocus = elemZone?.querySelectorAll<HTMLElement>(
      focusableElementQueryList.join(', ')
    );

    for (let i = 0; i < elemChildToFocus.length; i++) {
      const elem = elemChildToFocus.item(i);
      if (
        getComputedStyle(elem)?.display !== 'none' &&
        getComputedStyle(elem)?.visibility !== 'hidden'
      ) {
        elem.focus?.();
        return true;
      }
    }
    return false;
  }
}
